<template>
    <div class="card room-status-card position-relative">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="false"
                :showAddNew="false"
                title="Room Status"
            />
        </div>

        <div class="px-2 mt-2">
            <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div class="d-flex align-items-center gap-2">
                    <button
                        @click="navigatePrevious"
                        class="btn btn-outline-primary btn-sm"
                        :disabled="loading"
                        aria-label="Previous"
                    >
                        <i class="fa fa-angles-left"></i>
                    </button>

                    <div class="badge bg-light text-dark fs-6 py-1 px-3">
                        {{ currentPeriodLabel }}
                    </div>

                    <button
                        @click="navigateNext"
                        class="btn btn-outline-primary btn-sm"
                        :disabled="loading"
                        aria-label="Next"
                    >
                        <i class="fa fa-angles-right"></i>
                    </button>
                </div>

                <div class="d-flex flex-wrap gap-3 status-legend">
                    <span><i class="legend-dot free"></i> Available</span>
                    <span><i class="legend-dot reserved"></i> Reserved</span>
                    <span><i class="legend-dot occupied"></i> Occupied</span>
                </div>

                <div class="btn-group">
                    <button
                        @click="goToToday"
                        type="button"
                        class="btn btn-sm btn-info"
                        :disabled="loading"
                    >
                        Current
                    </button>
                    <button
                        v-for="type in VIEW_TYPES"
                        :key="type.value"
                        @click="onViewTypeSelect(type.value)"
                        type="button"
                        class="btn btn-sm"
                        :class="viewType === type.value ? 'btn-primary' : 'btn-outline-primary'"
                        :disabled="loading"
                    >
                        {{ type.label }}
                    </button>
                </div>
            </div>
        </div>

        <div class="px-2 mt-3">
            <div class="calendar-scroll" v-if="hasRooms">
                <div class="calendar-grid" :style="gridStyle">
                    <div class="calendar-corner"></div>
                    <div
                        v-for="day in dayColumns"
                        :key="day.key"
                        class="calendar-head-cell"
                        :class="{ 'is-today': day.key === todayKey }"
                    >
                        {{ day.label }}
                    </div>

                    <template v-for="group in groupedRooms" :key="group.id">
                        <div class="calendar-group-row" :style="groupRowStyle">
                            {{ group.name }}
                        </div>

                        <template v-for="room in group.rooms" :key="room.id">
                            <div class="calendar-room-label is-clickable" @click="openRoomModal(room)">{{ room.name }}</div>
                            <div class="calendar-room-track" :style="trackStyle">
                                <div
                                    v-for="cell in roomDayCells(room)"
                                    :key="cell.day.key"
                                    class="booking-bar"
                                    :class="[`bar-${cell.status.kind}`, { 'is-clickable': cell.status.kind !== 'free' }]"
                                    :title="cellTitle(cell.status)"
                                    @click="onCellClick(room, cell.day, cell.status)"
                                >
                                    {{ cell.status.label }}
                                </div>
                            </div>
                        </template>
                    </template>
                </div>
            </div>
            <p v-else class="text-muted px-1">No rooms found.</p>
        </div>

        <div class="px-2 mt-3">
            <Pagination :offset="params.offset" @onPageChange="onPageChange"/>
        </div>

        <Loader v-if="loading"/>

        <Teleport to="body">
            <RoomHistoryModal
                v-if="historyRoom"
                :room-id="historyRoom.id"
                :room-name="historyRoom.name"
                :day-kind="historyDayKind"
                :day-label="historyDay.key"
                :booking="historyBooking"
                @onClose="closeHistoryModal"
                @onStatusUpdated="onRoomStatusUpdated"
            />
        </Teleport>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import Loader from '@/components/atom/LoaderComponent';
import Pagination from '@/components/atom/Pagination';
import RoomHistoryModal from '@/components/molecule/company/hotel/hotel-room-status/RoomHistoryModal.vue';
import { computed, inject, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import handleHotel from '@/services/modules/hotel/hotel';
import useDate from '@/services/utils/day';

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const showError = inject('showError');
const historyRoom = ref(null);
const historyDayKind = ref('free');
const historyDay = ref(null);
const historyBooking = ref(null);
const { fetchHotelRoomAvailability } = handleHotel();
const { formatToDate } = useDate();

const loading = ref(false);
const rooms = ref([]);
const currentDate = ref(new Date());
const availabilityRequestId = ref(0);
const params = reactive({
    offset: 20,
});

const VIEW_TYPE_VALUES = {
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
};

const VIEW_TYPES = [
    { value: VIEW_TYPE_VALUES.DAILY, label: 'Daily' },
    { value: VIEW_TYPE_VALUES.WEEKLY, label: 'Weekly' },
    { value: VIEW_TYPE_VALUES.MONTHLY, label: 'Monthly' },
];

const viewType = ref(VIEW_TYPE_VALUES.WEEKLY);
const reservedStatuses = ['confirmed', 'pending'];

const hasRooms = computed(() => rooms.value.length > 0);

const todayKey = computed(() => formatToDate(new Date()));

const getStartOfWeek = (date) => {
    const clone = new Date(date);
    const day = clone.getDay();
    clone.setDate(clone.getDate() - day);
    return clone;
};

const rangeStart = computed(() => {
    const date = currentDate.value;
    if (viewType.value === VIEW_TYPE_VALUES.DAILY) return new Date(date);
    if (viewType.value === VIEW_TYPE_VALUES.WEEKLY) return getStartOfWeek(date);
    return new Date(date.getFullYear(), date.getMonth(), 1);
});

const rangeDayCount = computed(() => {
    const date = currentDate.value;
    if (viewType.value === VIEW_TYPE_VALUES.DAILY) return 1;
    if (viewType.value === VIEW_TYPE_VALUES.WEEKLY) return 7;
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
});

const rangeEndExclusive = computed(() => {
    const end = new Date(rangeStart.value);
    end.setDate(end.getDate() + rangeDayCount.value);
    return end;
});

const dayColumns = computed(() => {
    const columns = [];
    for (let i = 0; i < rangeDayCount.value; i++) {
        const day = new Date(rangeStart.value);
        day.setDate(rangeStart.value.getDate() + i);
        columns.push({
            key: formatToDate(day),
            label: day.toLocaleDateString('en-US', { day: '2-digit', month: 'short' }),
        });
    }
    return columns;
});

const formatDateShort = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const currentPeriodLabel = computed(() => {
    const date = currentDate.value;
    if (viewType.value === VIEW_TYPE_VALUES.DAILY) {
        return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    }
    if (viewType.value === VIEW_TYPE_VALUES.WEEKLY) {
        const startOfWeek = getStartOfWeek(date);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        return `${formatDateShort(startOfWeek)} - ${formatDateShort(endOfWeek)}`;
    }
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
});

const gridStyle = computed(() => {
    return { gridTemplateColumns: `160px repeat(${rangeDayCount.value}, minmax(70px, 1fr))` };
});

const groupRowStyle = computed(() => {
    return { gridColumn: `1 / span ${rangeDayCount.value + 1}` };
});

const trackStyle = computed(() => {
    return {
        gridColumn: `2 / span ${rangeDayCount.value}`,
        gridTemplateColumns: `repeat(${rangeDayCount.value}, minmax(70px, 1fr))`,
    };
});

const groupedRooms = computed(() => {
    const groups = [];
    const index = {};
    for (const room of rooms.value) {
        const key = room.group_id ? room.group_id : 'ungrouped';
        if (!index[key]) {
            index[key] = { id: key, name: room.group_name ? room.group_name : 'Others', rooms: [] };
            groups.push(index[key]);
        }
        index[key].rooms.push(room);
    }
    return groups;
});

const bookingKind = (booking) => {
    if (reservedStatuses.includes(booking.registration_status)) return 'reserved';
    return 'occupied';
};

const bookingOnDay = (room, dayKey) => {
    if (!room.bookings) return null;
    const match = room.bookings.find(booking => dayKey >= booking.check_in && dayKey < booking.check_out);
    if (!match) return null;
    return match;
};

const statusLabel = (kind) => {
    if (kind === 'reserved') return 'Reserved';
    return 'Occupied';
};

const cellTitle = (status) => {
    if (!status.reservation_no) return status.label;
    if (!status.guest_name) return `${status.label} (${status.reservation_no})`;
    return `${statusLabel(status.kind)} - ${status.guest_name} (${status.reservation_no})`;
};

const dayCellStatus = (room, day) => {
    const booking = bookingOnDay(room, day.key);
    if (!booking) {
        return { kind: 'free', label: 'Available', reservation_no: '', guest_name: '', booking: null };
    }
    const kind = bookingKind(booking);
    const guestName = booking.guest_name || '';
    const label = guestName || statusLabel(kind);
    return {
        kind,
        label,
        reservation_no: booking.reservation_no,
        guest_name: guestName,
        booking,
    };
};

const roomDayCellsCache = computed(() => {
    const cache = {};
    for (const room of rooms.value) {
        cache[room.id] = dayColumns.value.map(day => ({ day, status: dayCellStatus(room, day) }));
    }
    return cache;
});

const roomDayCells = (room) => {
    return roomDayCellsCache.value[room.id];
};

const updateRouteQuery = () => {
    $router.push({
        query: {
            ...$route.query,
            start: formatToDate(rangeStart.value),
            end: formatToDate(rangeEndExclusive.value),
            view: viewType.value,
        },
    });
};

const buildAvailabilityQuery = () => {
    let query = `?company_id=${$route.params.companyId}`;
    query += `&check_in=${formatToDate(rangeStart.value)}`;
    query += `&check_out=${formatToDate(rangeEndExclusive.value)}`;
    if ($route.query.page) query += '&page=' + $route.query.page;
    query += '&offset=' + params.offset;
    return query;
};

const setPagination = (data) => {
    if (!data.data.length) return;
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    });
};

const getAvailability = async () => {
    availabilityRequestId.value += 1;
    const requestId = availabilityRequestId.value;
    loading.value = true;
    await fetchHotelRoomAvailability(buildAvailabilityQuery()).then(res => {
        if (requestId !== availabilityRequestId.value) return;
        if (!res.status) {
            rooms.value = [];
            $store.commit('resetPagination');
            return;
        }
        rooms.value = res.data.data;
        setPagination(res.data);
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        if (requestId === availabilityRequestId.value) loading.value = false;
    });
};

const onCellClick = (room, day, status) => {
    if (status.kind === 'free') return;
    historyRoom.value = room;
    historyDayKind.value = status.kind;
    historyDay.value = day;
    historyBooking.value = status.booking;
};

const openRoomModal = (room) => {
    historyRoom.value = room;
    historyDayKind.value = 'free';
    historyDay.value = { key: '' };
    historyBooking.value = null;
};

const closeHistoryModal = () => {
    historyRoom.value = null;
    historyDay.value = null;
    historyBooking.value = null;
};

const onRoomStatusUpdated = () => {
    closeHistoryModal();
    getAvailability();
};

const navigatePrevious = () => {
    const date = new Date(currentDate.value);
    if (viewType.value === VIEW_TYPE_VALUES.DAILY) date.setDate(date.getDate() - 1);
    if (viewType.value === VIEW_TYPE_VALUES.WEEKLY) date.setDate(date.getDate() - 7);
    if (viewType.value === VIEW_TYPE_VALUES.MONTHLY) date.setMonth(date.getMonth() - 1);
    currentDate.value = date;
    updateRouteQuery();
    getAvailability();
};

const navigateNext = () => {
    const date = new Date(currentDate.value);
    if (viewType.value === VIEW_TYPE_VALUES.DAILY) date.setDate(date.getDate() + 1);
    if (viewType.value === VIEW_TYPE_VALUES.WEEKLY) date.setDate(date.getDate() + 7);
    if (viewType.value === VIEW_TYPE_VALUES.MONTHLY) date.setMonth(date.getMonth() + 1);
    currentDate.value = date;
    updateRouteQuery();
    getAvailability();
};

const goToToday = () => {
    currentDate.value = new Date();
    updateRouteQuery();
    getAvailability();
};

const onViewTypeSelect = (type) => {
    viewType.value = type;
    updateRouteQuery();
    getAvailability();
};

const onPageChange = async (page) => {
    const routeQuery = Object.assign({}, $route.query);
    routeQuery.page = page;
    await $router.push({ path: $route.path, query: routeQuery });
    await getAvailability();
};

onMounted(async () => {
    await getAvailability();
});
</script>

<style scoped>
.room-status-card {
    min-height: 75vh;
    padding-bottom: 2rem;
}
.status-legend span {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.82rem;
    color: #4a5568;
}
.legend-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;
}
.legend-dot.free {
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
}
.legend-dot.reserved {
    background: #fde68a;
}
.legend-dot.occupied {
    background: #c4b5fd;
}
.calendar-scroll {
    overflow-x: auto;
    padding-bottom: 0.5rem;
}
.calendar-grid {
    display: grid;
    min-width: 600px;
    row-gap: 2px;
}
.calendar-corner {
    background: #dbeafe;
}
.calendar-head-cell {
    background: #dbeafe;
    text-align: center;
    font-size: 0.78rem;
    font-weight: 600;
    color: #1e3a8a;
    padding: 0.4rem 0.25rem;
}
.calendar-head-cell.is-today {
    background: #bfdbfe;
}
.calendar-group-row {
    background: #dbeafe;
    font-weight: 700;
    font-size: 0.85rem;
    color: #1f2937;
    padding: 0.35rem 0.5rem;
}
.calendar-room-label {
    display: flex;
    align-items: center;
    padding: 0.4rem 0.5rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: #374151;
    background: #f8fafc;
}
.calendar-room-label.is-clickable {
    cursor: pointer;
}
.calendar-room-track {
    display: grid;
    position: relative;
    background: #f1f5f9;
    min-height: 34px;
}
.booking-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.72rem;
    font-weight: 600;
    border-radius: 4px;
    margin: 3px 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.booking-bar.is-clickable {
    cursor: pointer;
}
.bar-reserved {
    background: #fde68a;
    color: #92400e;
    border: 1px solid #3b82f6;
}
.bar-occupied {
    background: #c4b5fd;
    color: #4c1d95;
}
.bar-free {
    background: #fff;
    color: #94a3b8;
    border: 1px dashed #e2e8f0;
}
</style>
